import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faArrowUp, faCircleNotch, faUser } from '@fortawesome/free-solid-svg-icons'
import clsx from 'clsx'
import ChatMessage from './chat-message'
import UseChat from '../../utils/useChat'
import axiosInstance from '../../utils/axiosInstance'
import MessageSkeltonBox from '../skelton/message-skelton-box'
import GetUser from '../../utils/getUser'
import { toast } from 'react-toastify'
import { ParseErrors } from '../../utils/ParseErrors'
import { Link } from 'react-router-dom'

export interface ISingleMessage {
    chaseId: number;
    createdAt: string;
    id: number;
    isRead: boolean;
    hideFromPortal: boolean;
    message: string;
    type: "dashboard" | "portal"
    updatedAt: string
    userId: number
}

interface IChatMessages {
    totalCount: number
    data: ISingleMessage[]
}

const ChatBox = () => {
    const { currentConversation, updateConversationLastMessage, setCurrentConversation } = UseChat()
    const user = GetUser()
    const messageList = useRef(null)
    const [messages, setMessages] = useState<IChatMessages | undefined>(undefined)
    const [newMessage, setNewMessage] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(false)
    const [isSending, setIsSending] = useState<boolean>(false)
    const [isFetchingRecentMessages, setIsFetchingRecentMessages] = useState<boolean>(false)


    // --------------------------- [ SEND NEW MESSAGE ]------------------------
    const sendMessage = async () => {
        const messageData: any = {
            chaseId: currentConversation.id,
            userId: user.id,
            message: newMessage,
            type: "dashboard",
        }
        messages.data.push(messageData)
        setIsSending(true)
        try {
            setNewMessage("")
            updateConversationLastMessage(messageData)
            await axiosInstance.post("/message/send", messageData)
            setIsSending(false)
        } catch (error) {
            console.log(error)
            toast.error(ParseErrors(error))
            setIsSending(false)
        }
    }


    // ------------------------------- [ FIND MESSAGES ] --------------------------

    useEffect(() => {
        setLoading(true)
        const findMessages = async () => {
            try {
                const res = await axiosInstance.get(`/message/${currentConversation?.id}`)
                setMessages(res.data)
                setLoading(false)
            } catch (error) {
                console.log(error)
                setLoading(false)
            }
        }

        currentConversation.id && findMessages()
    }, [currentConversation.id])


    // --------------------------------- [ LOAD RECENT CHATS ] ------------------------
    const loadRecentChats = async () => {
        setIsFetchingRecentMessages(true)
        try {
            const res = await axiosInstance.get(`/message/${currentConversation.id}?offset=${(messages?.data.length)}&limit=${10}`)
            setMessages((prev) => ({ totalCount: res.data.totalCount, data: [...res.data.data, ...prev.data] }))
            setTimeout(() => {
                setIsFetchingRecentMessages(false)
            }, 500);
        } catch (error) {
            console.log(error)
            setIsFetchingRecentMessages(false)
        }
    }

    // ----------------------- [ ON ENTER SEND MESSAGE ] --------------------------
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && newMessage && !isSending) {
            sendMessage()
        }
    }


    // ---------------------------- [ SCROLL TO BOTTOM ] -------------------------
    useEffect(() => {
        if (messageList.current && !isFetchingRecentMessages) {
            messageList.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [messages?.data?.length])

    return (
        <div className='flex-1 min-w-0 h-full flex flex-col bg-background'>

            {/* ---------------------- CHAT HEADER -------------------------- */}
            <div className='shrink-0 px-6 py-4 bg-surface border-b border-edge flex items-center gap-3 smallTablet:px-4 smallTablet:py-3'>
                <button
                    type='button'
                    onClick={() => setCurrentConversation(undefined)}
                    title='Back to conversations'
                    className='hidden smallTablet:flex h-8 w-8 shrink-0 rounded-lg border border-edge bg-overlay items-center justify-center text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200'
                >
                    <FontAwesomeIcon icon={faArrowLeft} className='text-[12px]' />
                </button>

                <div className='h-10 w-10 shrink-0 rounded-full border border-edge bg-overlay-mid flex items-center justify-center'>
                    <FontAwesomeIcon icon={faUser} className='text-[14px] text-light-gray' />
                </div>

                <div className='min-w-0'>
                    <h2 className='text-[15px] font-bold text-text-primary truncate capitalize leading-tight'>
                        {currentConversation.firstName + " " + currentConversation.surname}
                    </h2>
                    <Link
                        to={`/chaselist?query=${currentConversation.policyReferenceNumber}`}
                        className='text-[12px] font-medium text-blue hover:text-accent-bright transition-colors'
                    >
                        {currentConversation.policyReferenceNumber}
                    </Link>
                </div>
            </div>
            {/* ---------------------- CHAT HEADER -------------------------- */}


            {/* --------------------- CONVERSATION MESSAGES ---------------------- */}
            <div className='w-full h-full overflow-y-auto py-6 custom-scrollbar smallTablet:min-h-0 smallTablet:py-4'>
                {messages?.totalCount !== messages?.data?.length && !isFetchingRecentMessages ? (
                    <div className='w-full flex justify-center pb-4'>
                        <button
                            type='button'
                            onClick={loadRecentChats}
                            className='h-8 px-4 rounded-full border border-edge text-[12px] font-semibold text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200'
                        >
                            Load earlier messages
                        </button>
                    </div>
                ) : null}

                {loading || isFetchingRecentMessages ?
                    <div className='w-[92%] max-w-[760px] mx-auto'>  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => {
                        return <MessageSkeltonBox key={item} isSender={item % 2 === 0} />
                    })} </div> : null}

                {messages?.data ? <div className='w-[92%] max-w-[760px] mx-auto'>
                    {messages.data.map((item, index) => {
                        return <ChatMessage key={item.id} item={item} showMessage={new Date(messages?.data[index + 1]?.createdAt)?.getMinutes() === new Date(item?.createdAt)?.getMinutes() && messages?.data[index + 1]?.type === item?.type} />
                    })}
                    <div ref={messageList}></div>
                </div> : null}
            </div>
            {/* --------------------- CONVERSATION MESSAGES ---------------------- */}


            {/* ----------------------- MESSAGE INPUT ------------------------ */}
            <div className='shrink-0 w-full bg-surface border-t border-edge px-6 py-4 smallTablet:px-3 smallTablet:py-3'>
                <div className='w-[92%] max-w-[760px] mx-auto smallTablet:w-full h-11 pl-4 pr-1 flex items-center gap-2 bg-input-bg border border-input-border focus-within:border-indigo-400/40 focus-within:ring-2 focus-within:ring-indigo-400/20 rounded-full transition-all duration-200'>
                    <input
                        value={newMessage}
                        onKeyDown={handleKeyDown}
                        className='flex-1 min-w-0 outline-none bg-transparent text-[13px] text-text-primary font-medium placeholder:text-light-gray'
                        placeholder='Write a message...'
                        onChange={(e) => setNewMessage(e.target.value)}
                    />

                    <button
                        disabled={isSending || !newMessage}
                        title='Send message'
                        className='h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-white bg-gradient-to-r from-accent to-accent-bright shadow-glow-sm hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0 disabled:cursor-not-allowed transition-all duration-200'
                        onClick={sendMessage}
                    >
                        <FontAwesomeIcon
                            icon={isSending ? faCircleNotch : faArrowUp}
                            className={clsx('text-[13px]', isSending && 'fa-spin')}
                        />
                    </button>
                </div>
            </div>
            {/* ----------------------- MESSAGE INPUT ------------------------ */}


        </div>
    )
}

export default ChatBox
